/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { ReactNode, useContext, useMemo } from 'react'
import { Table as RSTable, Thead, Tbody, Tr, Th, Td } from 'react-super-responsive-table'
import UserContext from '../../../UserContext'
import get from 'lodash/get'
import Paginator from './Paginator'
import type { Sortable, PageFilters } from '../../../models/interfaces'

type SortDirectionEnum = 'asc' | 'desc' | undefined
export type SortTuple = [string, SortDirectionEnum]

type ColumnProp<T> = {
    title?: string
    dataIndex: string
    render?: (value: any, record: T, index: number) => ReactNode
    showForRoles?: string[]
    hideForRoles?: string[]
    ignoreClick?: boolean
    sortable?: boolean
    className?: string
    customHeadAction?: () => void
    headRender? : () => ReactNode
}

export type ColumnProps<T> = ColumnProp<T>[]

type TableProps<T, U> = {
    data: T[]
    rowClassName?: (row : T) => string
    filters?: U & Sortable & PageFilters
    setFilters?: React.Dispatch<U & Sortable & PageFilters>
    totalRows?: number
    columns: ColumnProps<T>
    loading?: boolean
    error?: boolean
    onRowClick?: (record: T) => void
    disableMultiColumnSort?: boolean
    defaultPageSize?: number
    noDataMessage?: string
}

const toggleSort = (v: SortDirectionEnum) => v === undefined ? 'desc' : v === 'desc' ? 'asc' : undefined
//When only allowing one column to be sorted, disable being able to unsort a column.
const toggleSortSingleColumn = (v: SortDirectionEnum) => v === undefined ? 'desc' : v === 'desc' ? 'asc' : 'desc'
const handleSort = <T extends Sortable>(filterValues: T, setFilterValues: React.Dispatch<T>) => {
    return (s: SortTuple) => {
        const [key, value] = s

        if (value === undefined) return setFilterValues({ ...filterValues, sort: filterValues.sort?.filter(v => v[0] !== key) })

        const isSorted = filterValues.sort?.find(v => v[0] === key)

        return isSorted
            ? setFilterValues({ ...filterValues, sort: filterValues.sort?.map(v => v[0] === key ? s : v) })
            : setFilterValues({ ...filterValues, sort: [...filterValues.sort ?? [], s] })
    }
}

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
const Table = <T extends Record<string, any>, U>({ data, columns, loading, error, onRowClick, totalRows, filters, setFilters, disableMultiColumnSort, rowClassName, defaultPageSize, noDataMessage }: TableProps<T, U> & { children?: ReactNode }) => {
    const { hasAnyRole } = useContext(UserContext)

    const activeColumns = useMemo(() => {
        const showColumn = (hide?: string[], show?: string[]) => hide ? !hasAnyRole(...hide) : show ? hasAnyRole(...show) : true
        return columns.filter(c => showColumn(c.hideForRoles, c.showForRoles))
    }, [columns, hasAnyRole])

    const handleHeadClick = (c: ColumnProp<T>) => () => {
        if (!loading) {
            if (c.customHeadAction) {
                c.customHeadAction()
            }
            else if (!loading && c.sortable) {
                if (!filters || !setFilters) return
                const existingSort = filters?.sort?.find(f => f[0] === c.dataIndex)
                const direction = disableMultiColumnSort 
                    ? toggleSortSingleColumn(existingSort ? existingSort[1] : undefined)
                    : toggleSort(existingSort ? existingSort[1] : undefined)
                if (disableMultiColumnSort)
                    filters.sort = [];
                handleSort(filters, setFilters)([c.dataIndex, direction])
            }
        }
    }

    return (
        <>
            <RSTable>
                <Thead>
                    <Tr>
                        {
                            activeColumns.map((c, i) =>
                                <Th key={i} onClick={handleHeadClick(c)} className={`${c.sortable ? 'sortable ' : ''} ${c.className ? c.className : ''}`}>
                                    {c.headRender ? c.headRender() : c.title}
                                    {
                                        filters?.sort?.find(f => f[0] === c.dataIndex) !== undefined
                                            ? <i className={`fas fa-sort-amount-${(filters?.sort?.find(f => f[0] === c.dataIndex) || [])[1] === 'asc' ? 'down-alt' : 'up'}`}></i>
                                            : c.sortable ? <i className="fas fa-sort"></i> : undefined
                                    }
                                </Th>
                            )
                        }
                    </Tr >
                </Thead>
                <Tbody>
                    {!loading && !error && data?.map((r, i) =>
                        <Tr key={i} className={rowClassName ? rowClassName(r) : undefined}>
                            {activeColumns.map((c, j) =>
                                <Td key={j} className={c.className} onClick={() => onRowClick && !c.ignoreClick ? onRowClick(r) : undefined}>
                                    <div>
                                        {c.render ? c.render(get(r, c.dataIndex), r, i + j) : get(r, c.dataIndex)}
                                    </div>
                                </Td>
                            )}
                        </Tr>
                    )}

                    {loading && <DisplayRow children={<DataLoading />} />}
                    {!loading && !error && data.length === 0 && <DisplayRow children={noDataMessage || 'No Records Found'} />}
                    {!loading && error && <DisplayRow children="There was an error pulling the data" />}
                </Tbody>
            </RSTable>

            {totalRows && filters && setFilters
                ? <Paginator
                    defaultPageSize={defaultPageSize}
                    currentPage={Math.floor((filters.rowIndex) / (filters.pageSize)) + 1}
                    total={totalRows}
                    onChange={(page, pageSize) => setFilters({ ...filters, pageSize, rowIndex: (page - 1) * pageSize })}
                />
                : null
            }
        </>
    )
}

const DataLoading: React.FC = () => <>
    <div><img src="/spinning-circles.svg" alt="loading" /></div>
    <div className='loading-spinner-text'><pre>Loading...</pre></div>
</>

const DisplayRow: React.FC = ({ children }) => <tr>
    <td colSpan={9999} className='no-hover'>
        <div className='message-display-row'><span className='message'>{children}</span></div>
    </td>
</tr>

export { DataLoading }

export default Table
